import { aspectOf, exportSizesFor } from "@veyso/core";

/**
 * The sizes on offer, at the shape the project actually is.
 *
 * This was a fixed list of three 16:9 sizes, which was right while every
 * project was 16:9. It stopped being right the moment the editor could make a
 * vertical one: rendering a 9:16 timeline at 1920×1080 does not letterbox it,
 * it *reflows* it — every card element is stored as a fraction of the frame,
 * so they would all be re-laid out for a shape nobody composed them in.
 *
 * So the list is derived from the timeline. The shape is the project's; only
 * the resolution is a choice.
 *
 * 1080 first, and deliberately. The default is picked by frame rate, so
 * whichever 30 and 60 come first is what an untouched project encodes at.
 */
export interface Preset {
  label: string;
  width: number;
  height: number;
  fps: 30 | 60;
  /** Shown, and not selectable. See the 4K note below. */
  comingSoon: boolean;
  use: string;
}

/** The project's own shape, or 16:9 before one has loaded. */
const DEFAULT_SIZE = { width: 1920, height: 1080 };

/**
 * Familiar where a convention exists, explicit where one does not.
 *
 * "1080p" means one thing for a wide frame and nothing for a tall one, so a
 * vertical export says 1080×1920 rather than borrowing a name that would have
 * to be explained.
 */
function sizeLabel(width: number, height: number): string {
  if (width / height > 1.7) return `${height}p`;
  return `${width}×${height}`;
}

export function presetsFor(timeline: { width: number; height: number } | null): Preset[] {
  const size = timeline ?? DEFAULT_SIZE;
  const sizes = exportSizesFor(size);
  const [full, small] = sizes;

  const at = (option: (typeof sizes)[number], fps: 30 | 60, use: string): Preset => ({
    label: `${sizeLabel(option.width, option.height)} · ${fps}fps`,
    width: option.width,
    height: option.height,
    fps,
    comingSoon: false,
    use,
  });

  const presets: Preset[] = [
    at(full, 30, "Websites, docs and Slack"),
    at(full, 60, "YouTube and product demos"),
    at(small, 30, "Email and quick shares"),
  ];

  /**
   * Shown, and not selectable, and only on 16:9.
   *
   * A worker can render 4K; what it costs there is the problem — no graphics
   * hardware, so every frame is rasterised in software, and a 95-second 1080p
   * video took 82 minutes. An option whose honest description is "most of a
   * working day" is not one to offer, and "coming soon" says why rather than
   * leaving somebody to wonder. Parallel rendering on Lambda is the work it
   * waits on.
   *
   * Not offered on the other shapes because nobody has asked to hand a
   * vertical video to a projector, and a coming-soon tile for something
   * nobody wants is noise.
   */
  if (aspectOf(size) === "16:9") {
    presets.push(
      {
        label: "4K · 30fps",
        width: 3840,
        height: 2160,
        fps: 30,
        comingSoon: true,
        use: "Presentations and big screens",
      },
      {
        label: "4K · 60fps",
        width: 3840,
        height: 2160,
        fps: 60,
        comingSoon: true,
        use: "Big screens, smoothest motion",
      },
    );
  }

  return presets;
}

/**
 * Every preset, with its place in its own list.
 *
 * The index is carried rather than recomputed, because the selection is an
 * index into whatever `presetsFor` returned — and that list changes length
 * with the project's shape, which is exactly why the pairing matters.
 */
export function offeredPresets(timeline: { width: number; height: number } | null): {
  option: Preset;
  index: number;
}[] {
  return presetsFor(timeline).map((option, index) => ({ option, index }));
}

/** Whether this preset can actually be picked. */
export function isSelectable(option: Preset): boolean {
  return !option.comingSoon;
}
